/**
 * Keltner Channel
 *
 * Volatility-based envelope using EMA as the middle band and ATR for
 * the upper/lower bands.
 * Middle = EMA(close, period)
 * Upper  = Middle + multiplier × ATR(period)
 * Lower  = Middle − multiplier × ATR(period)
 * Default: period=20, multiplier=2.
 *
 * Category: Volatility | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeEMA } from "./sma";

export const keltnerIndicator: IndicatorDefinition = {
  id: "keltner",
  name: "Keltner Channel",
  shortName: "Keltner",
  category: "volatility",
  pane: "overlay",
  params: [
    { key: "emaPeriod", label: "EMA Period", type: "number", default: 20, min: 1, max: 500 },
    { key: "atrPeriod", label: "ATR Period", type: "number", default: 10, min: 1, max: 200 },
    { key: "multiplier", label: "Multiplier", type: "number", default: 1.5, min: 0.1, max: 10, step: 0.1 },
  ],
  plots: [
    { key: "upper", label: "Upper", color: "#26A69A", style: "line", lineWidth: 1 },
    { key: "middle", label: "Middle", color: "#BDBDBD", style: "line", lineWidth: 1 },
    { key: "lower", label: "Lower", color: "#26A69A", style: "line", lineWidth: 1 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { emaPeriod, atrPeriod, multiplier } = params;

    // Calculate EMA of close
    const closes = candles.map(c => c.close);
    const ema = computeEMA(closes, emaPeriod);

    // Calculate ATR
    const trueRanges: number[] = [candles[0].high - candles[0].low];
    for (let i = 1; i < candles.length; i++) {
      trueRanges.push(Math.max(
        candles[i].high - candles[i].low,
        Math.abs(candles[i].high - candles[i - 1].close),
        Math.abs(candles[i].low - candles[i - 1].close),
      ));
    }

    // Wilder's smoothing for ATR: first is SMA, then running average
    const atr: number[] = [];
    let atrSum = 0;
    for (let i = 0; i < trueRanges.length; i++) {
      if (i < atrPeriod) {
        atrSum += trueRanges[i];
        if (i === atrPeriod - 1) {
          atr.push(atrSum / atrPeriod);
        } else {
          atr.push(NaN);
        }
      } else {
        const prev = atr[atr.length - 1];
        atr.push((prev * (atrPeriod - 1) + trueRanges[i]) / atrPeriod);
      }
    }

    const upperData: { time: number; value: number }[] = [];
    const middleData: { time: number; value: number }[] = [];
    const lowerData: { time: number; value: number }[] = [];

    for (let i = 0; i < candles.length; i++) {
      if (isNaN(ema[i]) || isNaN(atr[i])) continue;
      const mid = ema[i];
      const band = multiplier * atr[i];
      upperData.push({ time: candles[i].time, value: mid + band });
      middleData.push({ time: candles[i].time, value: mid });
      lowerData.push({ time: candles[i].time, value: mid - band });
    }

    return [
      { key: "upper", data: upperData },
      { key: "middle", data: middleData },
      { key: "lower", data: lowerData },
    ];
  },
};
